import { Component, inject, signal, viewChild, effect, computed } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NewsService, News, NewsImage } from '../news.service';
import { Auth } from '@angular/fire/auth';
import { ImagePreview } from '../news-image/news-image.component';
import { NewsImageListComponent } from '../news-image-list/news-image-list.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-news-editor',
  imports: [ReactiveFormsModule, NewsImageListComponent, ConfirmDialogComponent],
  templateUrl: './news-editor.component.html',
  styleUrl: './news-editor.component.css'
})
export class NewsEditorComponent {
  private readonly fb = inject(FormBuilder);
  private readonly newsService = inject(NewsService);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly imagePreviews = signal<ImagePreview[]>([]);
  readonly isUploading = signal(false);
  readonly isDeleting = signal(false);
  readonly confirmingDelete = signal(false);
  readonly deleteError = signal<string | null>(null);
  readonly newsId = signal<string | null>(null);
  readonly isPublished = signal(false);

  readonly isEditMode = computed(() => this.newsId() !== null);
  readonly isSaved = computed(() => this.isEditMode() && !this.isPublished());
  
  readonly saveButtonText = computed(() => {
    if (this.isUploading()) return 'Saving...';
    if (!this.isEditMode()) return 'Save';
    if (this.isSaved()) return 'Publish';
    return 'Update';
  });

  readonly fileInput = viewChild<HTMLInputElement>('fileInput');

  readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.minLength(3)]],
    summary: ['', [Validators.required, Validators.maxLength(200)]],
    content: ['', [Validators.required, Validators.minLength(10)]],
  });

  get f() { return this.form.controls; }

  constructor() {
    effect(async () => {
      const id = this.route.snapshot.paramMap.get('id');
      
      if (id) {
        this.newsId.set(id);
        await this.loadNews(id);
      }
    });
  }

  private async loadNews(id: string): Promise<void> {
    try {
      const news = await this.newsService.getNewsById(id);
      if (!news) {
        console.error('News article not found');
        this.goBack();
        return;
      }

      this.isPublished.set(news.published);

      this.form.patchValue({
        title: news.title,
        summary: news.summary ?? '',
        content: news.content
      });

      if (news.newsImages.length > 0) {
        const imagesWithUrls = await this.newsService.getImagesWithMetadata(news.newsImages);
        this.imagePreviews.set(this.newsService.mapToImagePreviews(imagesWithUrls));
      }
    } catch (error) {
      console.error('Error loading news:', error);
      this.goBack();
    }
  }

  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const currentCount = this.imagePreviews().length;
    
    const newPreviews = await this.newsService.createImagePreviewsFromFile(files, currentCount);
    this.imagePreviews.set([...this.imagePreviews(), ...newPreviews]);
    input.value = '';
  }

  onRemoveImage(index: number): void {
    const previews = [...this.imagePreviews()];
    const preview = previews[index];
    
    if (!preview.isExisting && preview.file) {
      URL.revokeObjectURL(preview.url);
    }
    
    previews.splice(index, 1);
    
    const reorderedPreviews = previews.map((p, i) => ({ ...p, order: i }));
    this.imagePreviews.set(reorderedPreviews);
  }

  onReorderImages(event: { from: number; to: number }): void {
    const previews = [...this.imagePreviews()];
    const [draggedPreview] = previews.splice(event.from, 1);
    previews.splice(event.to, 0, draggedPreview);
    
    const reorderedPreviews = previews.map((p, i) => ({ ...p, order: i }));
    this.imagePreviews.set(reorderedPreviews);
  }

  goBack(): void {
    this.imagePreviews()
      .filter(p => !p.isExisting && p.file)
      .forEach(preview => URL.revokeObjectURL(preview.url));
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const user = this.auth.currentUser;
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    this.isUploading.set(true);
    const { title, summary, content } = this.form.getRawValue();
    
    try {
      // Determine if we should publish
      const shouldPublish = this.isSaved() || this.isPublished();

      if (this.isEditMode() && this.newsId()) {
        // Update existing news
        const newsImages = await this.#convertPreviewsToNewsImages(this.newsId()!);
        
        await this.newsService.updateNews(this.newsId()!, {
          title,
          summary,
          content,
          published: shouldPublish,
          newsImages: newsImages
        });
        
        this.isPublished.set(shouldPublish);
        console.log(`News ${shouldPublish ? 'published' : 'updated'}`);
      } else {
        // Create new news (always save as draft first)
        const newsId = await this.newsService.createNews({
          title,
          summary,
          content,
          author: user.displayName ?? 'Unknown',
          authorId: user.uid,
          published: false,
          sendNotification: false,
          tags: []
        });
        
        if (this.imagePreviews().length > 0) {
          const newsImages = await this.#convertPreviewsToNewsImages(newsId);
          await this.newsService.updateNews(newsId, { newsImages: newsImages });
        }
        
        this.newsId.set(newsId);
        this.isPublished.set(false);
        console.log('News saved as draft with ID:', newsId);
        
        // Update URL to edit mode without navigation
        this.router.navigate(['/main/news', newsId], { replaceUrl: true });
      }
    } catch (error) {
      console.error('Error saving news:', error);
    } finally {
      this.isUploading.set(false);
    }
  }

  async #convertPreviewsToNewsImages(newsId: string): Promise<NewsImage[]> {
    const newsImages: NewsImage[] = [];
    const previews = this.imagePreviews();
    
    if (this.isEditMode()) {
      const existingNews = await this.newsService.getNewsById(newsId);
      if (existingNews) {
        const currentPaths = previews
          .filter(p => p.isExisting && p.storagePath)
          .map(p => p.storagePath!);
        
        const pathsToDelete = existingNews.newsImages
          .map(img => img.storagePath)
          .filter(path => !currentPaths.includes(path));
        
        await Promise.allSettled(
          pathsToDelete.map(path => this.newsService.deleteImage(path))
        );
      }
    }
    
    for (const preview of previews) {
      if (preview.file) {
        const newsImage = await this.newsService.uploadImage(newsId, preview.file, preview.order);
        newsImages.push(newsImage);
      } else if (preview.storagePath) {
        newsImages.push({
          storagePath: preview.storagePath,
          order: preview.order,
          uploadedAt: preview.uploadedAt!,
          ...(preview.altText && { altText: preview.altText }),
          ...(preview.caption && { caption: preview.caption })
        });
      }
    }
    
    return newsImages;
  }

  delete(): void {
    if (!this.newsId()) return;
    this.deleteError.set(null);
    this.confirmingDelete.set(true);
  }

  cancelDelete(): void {
    this.confirmingDelete.set(false);
  }

  async confirmDelete(): Promise<void> {
    const id = this.newsId();
    if (!id) return;

    this.isDeleting.set(true);
    this.deleteError.set(null);

    try {
      await this.newsService.deleteNews(id);
      console.log('News article deleted successfully');
      this.confirmingDelete.set(false);
      this.router.navigate(['/main/news']);
    } catch (error) {
      console.error('Error deleting news:', error);
      this.deleteError.set('Failed to delete news article. Please try again.');
    } finally {
      this.isDeleting.set(false);
    }
  }
}
